import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";
import { api } from "@/lib/api";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function renderMarkdown(md) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} >
      {md}
    </ReactMarkdown>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-24 font-mono text-xs uppercase tracking-widest text-[#94A3B8]">Loading...</div>;
  }
  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="blog-not-found">
        <div className="overline mb-3">404</div>
        <h1 className="font-display font-black text-white text-4xl mb-4">Post not found</h1>
        <Link to="/blog" className="text-[#FF5500] hover:text-[#FF7326] underline underline-offset-4">← Back to blog</Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid={`blog-post-detail-${post.slug}`}>
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white mb-8">
        <ArrowLeft className="w-4 h-4" /> All posts
      </Link>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="overline">
          {new Date(post.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}
        </span>
        {post.tags && post.tags.length > 0 && (
          <span className="font-mono text-xs text-[#94A3B8] flex items-center gap-1">
            <Tag className="w-3 h-3" /> {post.tags.join(" · ")}
          </span>
        )}
        <span className="font-mono text-xs text-[#94A3B8]">by {post.author}</span>
      </div>

      <h1 className="font-display font-black text-white text-4xl sm:text-5xl tracking-tighter mb-8 leading-tight">
        {post.title}
      </h1>

      {post.cover_image && (
        <img src={post.cover_image} alt="" className="w-full h-64 sm:h-80 object-cover border border-[#272E3A] mb-10" />
      )}

      <div className="prose-mesh text-lg" data-testid="blog-post-content">{renderMarkdown(post.content)}</div>
    </article>
  );
}
